import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { KeyRound, Webhook, Copy, Trash2, Send, RefreshCw, Eye } from "lucide-react";
import { useBrand } from "@/context/BrandContext";

const inputCls = "bg-white border-zinc-200 rounded-sm h-9 font-mono text-[12px] focus-visible:ring-orange-500/40";
const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast("Copied to clipboard"));

export function ApiKeysSection() {
  const { user } = useAuth();
  const brand = useBrand();
  const isOwner = user?.role === "owner";
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("Marketplace Deal Options (production)");
  const [fresh, setFresh] = useState(null);

  const load = () => endpoints.apiKeys().then((r) => setKeys(r.keys));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await endpoints.createApiKey(name);
      toast.success("Key generated", { description: "Click “Reveal once” to view it — it is shown a single time." });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reveal = async (id) => {
    try {
      const r = await endpoints.revealApiKey(id);
      setFresh(r.key);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const revoke = async (id) => { await endpoints.revokeApiKey(id); load(); };

  return (
    <div className="border border-zinc-200 p-6" data-testid="api-keys-section">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-4 h-4 text-orange-600" />
        <span className="tx-overline">Inbound hand-offs · API keys</span>
      </div>
      <p className="text-[12px] text-zinc-500 mb-4">{brand.source_app_name} authenticates with <span className="font-mono text-zinc-700">X-API-Key</span> when pushing deals to <span className="font-mono text-zinc-700">POST /api/handoff/deals</span>.</p>
      {isOwner && (
        <div className="flex gap-2 mb-4">
          <Input data-testid="api-key-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Key label" />
          <Button onClick={create} data-testid="create-api-key-button" className="bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm h-9">Generate key</Button>
        </div>
      )}
      {fresh && (
        <div data-testid="fresh-api-key" className="border border-orange-500/40 bg-orange-50 p-3 mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="tx-overline text-orange-600">Copy now — this key will not be shown again</span>
            <button onClick={() => setFresh(null)} data-testid="dismiss-api-key-button" className="text-[10px] font-mono text-zinc-500 hover:text-zinc-900">dismiss</button>
          </div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-[12px] text-zinc-900 break-all flex-1" data-testid="fresh-api-key-value">{fresh}</code>
            <button onClick={() => copy(fresh)} data-testid="copy-api-key-button" className="text-zinc-600 hover:text-zinc-900"><Copy className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}
      <div className="divide-y divide-zinc-200 border border-zinc-200" data-testid="api-keys-list">
        {keys.length === 0 && <div className="px-4 py-4 text-[12px] text-zinc-500">No keys yet.</div>}
        {keys.map((k) => (
          <div key={k.id} data-testid="api-key-row" className={`px-4 py-2.5 flex items-center justify-between ${k.revoked ? "opacity-40" : ""}`}>
            <div>
              <div className="text-[12px] text-zinc-800">{k.name} {k.revoked && <span className="text-rose-600 font-mono text-[10px] ml-2">REVOKED</span>}</div>
              <div className="font-mono text-[11px] text-zinc-500">{k.prefix}…  · last used {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}</div>
            </div>
            <div className="flex items-center gap-3">
              {isOwner && !k.revoked && k.pending_reveal && (
                <button onClick={() => reveal(k.id)} data-testid="reveal-api-key-button"
                        className="h-7 px-2.5 border border-orange-500/60 text-orange-700 bg-orange-50 hover:bg-orange-100 text-[11px] font-mono flex items-center gap-1.5">
                  <Eye className="w-3 h-3" /> Reveal once
                </button>
              )}
              {!k.revoked && !k.pending_reveal && <span className="font-mono text-[10px] text-zinc-400">revealed</span>}
              {isOwner && !k.revoked && (
                <button onClick={() => revoke(k.id)} data-testid="revoke-api-key-button" className="text-zinc-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function WebhookSection() {
  const { user } = useAuth();
  const brand = useBrand();
  const isOwner = user?.role === "owner";
  const [cfg, setCfg] = useState(null);
  const [deliveries, setDeliveries] = useState([]);

  const load = () => {
    endpoints.webhook().then(setCfg);
    endpoints.webhookDeliveries().then((r) => setDeliveries(r.deliveries));
  };
  useEffect(load, []);

  const save = async () => {
    try {
      const saved = await endpoints.saveWebhook({ url: cfg.url || null, enabled: cfg.enabled, events: cfg.events });
      setCfg((c) => ({ ...c, ...saved }));
      toast.success("Webhook saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const test = async () => {
    try {
      const d = await endpoints.testWebhook();
      toast[d.ok ? "success" : "error"](`Ping ${d.ok ? "delivered" : "failed"} · HTTP ${d.status_code ?? "—"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const rotate = async () => {
    const r = await endpoints.rotateWebhookSecret();
    setCfg((c) => ({ ...c, secret: r.secret }));
    toast("Secret rotated");
  };
  const toggleEvent = (ev) => setCfg((c) => ({ ...c, events: c.events.includes(ev) ? c.events.filter((e) => e !== ev) : [...c.events, ev] }));

  if (!cfg) return null;
  return (
    <div className="border border-zinc-200 p-6" data-testid="webhook-section">
      <div className="flex items-center gap-2 mb-1">
        <Webhook className="w-4 h-4 text-orange-600" />
        <span className="tx-overline">Status back to {brand.source_app_name} · signed webhook</span>
      </div>
      <p className="text-[12px] text-zinc-500 mb-4">{brand.app_name} POSTs <span className="font-mono text-zinc-700">run.*</span> events with <span className="font-mono text-zinc-700">X-Transactr-Signature: t=…,v1=HMAC-SHA256(secret, t.body)</span>.</p>
      <div className="space-y-3">
        <Input data-testid="webhook-url-input" value={cfg.url || ""} onChange={(e) => setCfg({ ...cfg, url: e.target.value })}
               disabled={!isOwner} placeholder="https://apom.example.com/webhooks/cee" className={inputCls} />
        <div className="flex flex-wrap gap-2">
          {cfg.available_events.map((ev) => (
            <button key={ev} type="button" disabled={!isOwner} onClick={() => toggleEvent(ev)} data-testid={`webhook-event-${ev}`}
                    className={`h-7 px-2.5 text-[11px] font-mono border transition-colors ${cfg.events.includes(ev) ? "border-orange-500/60 text-orange-600 bg-orange-50" : "border-zinc-200 text-zinc-500"}`}>
              run.{ev}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-[12px] text-zinc-700">
            <input type="checkbox" data-testid="webhook-enabled-checkbox" checked={!!cfg.enabled} disabled={!isOwner}
                   onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="accent-orange-600" />
            Enabled
          </label>
          {isOwner && (
            <div className="flex gap-2">
              <Button onClick={test} variant="outline" data-testid="webhook-test-button" className="border-zinc-300 hover:bg-zinc-100 rounded-sm h-8 gap-2 text-[12px]"><Send className="w-3 h-3" /> Send ping</Button>
              <Button onClick={save} data-testid="webhook-save-button" className="bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm h-8 text-[12px]">Save</Button>
            </div>
          )}
        </div>
        {isOwner && cfg.secret && (
          <div className="border border-zinc-200 bg-white p-3 flex items-center gap-2">
            <span className="tx-overline shrink-0">Secret</span>
            <code className="font-mono text-[11px] text-zinc-700 break-all flex-1" data-testid="webhook-secret">{cfg.secret}</code>
            <button onClick={() => copy(cfg.secret)} className="text-zinc-600 hover:text-zinc-900"><Copy className="w-3.5 h-3.5" /></button>
            <button onClick={rotate} data-testid="webhook-rotate-button" title="Rotate secret" className="text-zinc-600 hover:text-orange-600"><RefreshCw className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
      <div className="tx-overline mt-6 mb-2">Recent deliveries</div>
      <div className="border border-zinc-200 divide-y divide-zinc-200 max-h-[220px] overflow-y-auto" data-testid="webhook-deliveries">
        {deliveries.length === 0 && <div className="px-4 py-3 text-[12px] text-zinc-500">No deliveries yet.</div>}
        {deliveries.map((d) => (
          <div key={d.id} data-testid="webhook-delivery-row" className="px-4 py-2 flex items-center justify-between font-mono text-[11px]">
            <span className="text-zinc-700">{d.event} <span className="text-zinc-400">{d.run_id || ""}</span></span>
            <span className={d.ok ? "text-emerald-600" : d.retry_state === "scheduled" ? "text-amber-700" : "text-rose-600"} data-testid="delivery-status">
              {d.ok ? "OK" : d.retry_state === "scheduled" ? "RETRYING" : d.retry_state === "exhausted" ? "EXHAUSTED" : "FAIL"} {d.status_code ?? ""} · {d.attempts}x
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
